import React, { FC, useContext, useMemo } from 'react'

import { PijmaComponent } from '@pijma/helper-component'
import {
  ButtonProps,
  ButtonState,
  ButtonStylesStructure,
} from '@pijma/hero-types'
import { useStyles } from '@pijma/theme-provider'

import { ButtonBaseStyles } from './Button.style'
import { ButtonGroupContext } from './ButtonGroup'

export const Button: FC<ButtonProps> = ({
  type = 'primary',
  size = 'm',
  disabled = false,
  autoFocus = false,
  shape = 'default',
  htmlType = 'button',
  ...otherProps
}) => {
  const props = {
    type,
    size,
    disabled,
    autoFocus,
    shape,
    htmlType,
    ...otherProps,
  }
  const buttonContext = useContext(ButtonGroupContext)

  const state = useMemo<ButtonState>(
    () => ({
      isInButtonGroup: buttonContext.active,
      isButtonGroupStretch: buttonContext.stretch,
    }),
    [buttonContext],
  )

  const {
    classNames: {
      root,
      childrenWrapper,
      startIcon,
      endIcon,
      subtitle,
      loadingWrapper,
    },
    components: { StartIcon, EndIcon, Spinner },
  } = useStyles<ButtonStylesStructure>(
    ['Button'],
    { ...props, ...state },
    { defaultStyles: ButtonBaseStyles },
  )

  return (
    <PijmaComponent
      as="button"
      commonProps={props}
      className={root}
      type={props.htmlType}
      onClick={props.onPress}
      disabled={props.disabled}
    >
      {(props.startIcon || props.icon) && (
        <div className={startIcon}>{StartIcon}</div>
      )}
      <div className={childrenWrapper}>
        {props.children}
        {props.subtitle && <div className={subtitle}>{props.subtitle}</div>}
      </div>
      {props.endIcon && <div className={endIcon}>{EndIcon}</div>}
      {props.endIconAfterNode ?? null}
      {props.loading && <div className={loadingWrapper}>{Spinner}</div>}
    </PijmaComponent>
  )
}

Button.displayName = 'Button'
